import { CircleAlert, CircleCheck, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { cx } from '../../lib/cx';
import { useT } from '../../lib/i18n';
import { commonMessages } from '../../lib/i18n/messages/common';
import { ToastContext, type ToastOptions } from './toast-context';

interface QueuedToast extends ToastOptions {
  id: number;
}

/**
 * DESIGN 5.12: one toast at a time (queued), bottom, above the tab bar or Save bar
 * (`--toast-bottom`), pauses on hover/focus, `role="status"` or `role="alert"` for errors.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<QueuedToast[]>([]);
  const nextId = useRef(1);

  const show = useCallback((opts: ToastOptions | string) => {
    const o = typeof opts === 'string' ? { message: opts } : opts;
    setQueue((q) => [...q, { ...o, id: nextId.current++ }]);
  }, []);

  const current = queue[0];
  const dismiss = useCallback((id: number, expired: boolean) => {
    setQueue((q) => {
      const t = q.find((x) => x.id === id);
      if (expired) t?.onExpire?.();
      return q.filter((x) => x.id !== id);
    });
  }, []);

  return (
    <ToastContext value={show}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 z-[60] flex justify-center px-4"
        style={{ bottom: 'calc(var(--toast-bottom, 16px) + var(--safe-bottom))' }}
      >
        {current && <ToastView key={current.id} toast={current} onDone={dismiss} />}
      </div>
    </ToastContext>
  );
}

function ToastView({
  toast,
  onDone,
}: {
  toast: QueuedToast;
  onDone: (id: number, expired: boolean) => void;
}) {
  const t = useT(commonMessages);
  const [paused, setPaused] = useState(false);
  const tone = toast.tone ?? 'neutral';
  const duration = toast.duration ?? (tone === 'error' ? Infinity : toast.action ? 6000 : 4000);
  const remaining = useRef(duration);
  const started = useRef(0);

  useEffect(() => {
    if (paused || !Number.isFinite(remaining.current)) return;
    started.current = Date.now();
    const timer = setTimeout(() => onDone(toast.id, true), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current -= Date.now() - started.current;
    };
  }, [paused, onDone, toast.id]);

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="pointer-events-auto flex w-[min(92vw,420px)] animate-toast-in items-center gap-3 rounded-control bg-primary py-2 pr-2 pl-4 text-primary-fg shadow-pop"
    >
      {tone === 'error' ? (
        <CircleAlert className="ic ic-sm" strokeWidth={1.75} aria-hidden />
      ) : (
        <CircleCheck className="ic ic-sm" strokeWidth={1.75} aria-hidden />
      )}
      <p className="min-h-[28px] flex-1 py-1 text-sm font-medium">{toast.message}</p>
      {toast.action && (
        <button
          type="button"
          className={cx(
            'min-h-tap rounded-control px-3 text-sm font-bold text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          )}
          onClick={() => {
            toast.action?.onClick();
            onDone(toast.id, false);
          }}
        >
          {toast.action.label}
        </button>
      )}
      {(tone === 'error' || !toast.action) && (
        <button
          type="button"
          aria-label={t('close')}
          className="iconbtn !text-primary-fg hover:!bg-primary-fg/10 active:!bg-primary-fg/10"
          onClick={() => onDone(toast.id, true)}
        >
          <X className="ic ic-sm" strokeWidth={1.75} aria-hidden />
        </button>
      )}
    </div>
  );
}
